/**
 * A box as tall as the window has room for below where it starts (down to the page's own bottom padding), so what's
 * in it scrolls instead of the page: a document or spreadsheet, as an app's canvas would. Never shorter than `min`
 * (a short window scrolls the page to it, as before).
 */

import { useLayoutEffect } from "preact/hooks";
import { shell } from "./native.ts";

/**
 * How tall the window is for it: the window's (what shows), but in an app the layout's, which its keyboard leaves be
 * (what shows shrinks as it comes up: the box, made shorter and taller again, crept; there an editor's room for the
 * keyboard is its own, after its content).
 */
const viewport = (): number => (shell() ? document.documentElement.clientHeight : window.innerHeight);

/** The padding under the page's content (`main`'s), so the box ends where the page would. */
function bottomPadding(el: HTMLElement): number {
  const main = el.closest("main");
  return main ? Number.parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
}

export function useFillHeight(ref: { current: HTMLElement | null }, min = 384): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const size = () => {
      // Where it starts without its own top margin (one going out over what's above it, [data-bleed], may be on its
      // way), and as much taller as it goes out over the page's padding and above (its --bleed-y, eased as it goes).
      const top =
        el.getBoundingClientRect().top + window.scrollY - (Number.parseFloat(getComputedStyle(el).marginTop) || 0);
      el.style.height = `calc(${Math.max(min, viewport() - top - bottomPadding(el))}px + var(--bleed-y, 0px))`;
    };
    size();
    window.addEventListener("resize", size);
    // What's above it can change height (a banner, wrapped header): the page's content box says so.
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(size);
    const main = el.closest("main");
    if (main) {
      ro?.observe(main);
    }
    return () => {
      window.removeEventListener("resize", size);
      ro?.disconnect();
      el.style.height = "";
    };
  }, [ref, min]);
}
